import { cn } from "@/utils";
import { Check } from "lucide-react";
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

/**
 * Controles binários: Checkbox e Switch.
 *
 * Quando usar cada um:
 * - Checkbox → o valor só vale quando o formulário for salvo ("Aceito os termos").
 * - Switch   → a mudança tem efeito imediato ("Régua de cobrança ativa").
 *
 * Em ambos, a linha inteira (rótulo + descrição) é clicável: em tela pequena,
 * mirar num quadradinho de 20px é fonte garantida de erro.
 */

type ToggleBaseProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "size"
> & {
  label: ReactNode;
  description?: ReactNode;
  containerClassName?: string;
};

export const Checkbox = forwardRef<HTMLInputElement, ToggleBaseProps>(
  (
    {
      label,
      description,
      containerClassName,
      className,
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const controlId = id ?? generatedId;

    return (
      <label
        htmlFor={controlId}
        className={cn(
          "group flex min-h-11 items-start gap-3 py-1",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          containerClassName
        )}
      >
        <span className="relative mt-0.5 flex size-5 shrink-0 items-center justify-center">
          <input
            ref={ref}
            id={controlId}
            type="checkbox"
            disabled={disabled}
            className={cn(
              "peer rounded-ds-sm border-ds-ink-500 bg-ds-surface size-5 appearance-none border",
              "ease-ds-out transition-colors duration-150",
              "checked:border-ds-brand-600 checked:bg-ds-brand-600",
              "focus-visible:ring-ds-focus focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
              disabled ? "cursor-not-allowed" : "cursor-pointer",
              "motion-reduce:transition-none",
              className
            )}
            {...props}
          />
          <Check
            aria-hidden="true"
            strokeWidth={3}
            className="text-ds-on-solid pointer-events-none absolute size-3.5 opacity-0 peer-checked:opacity-100"
          />
        </span>

        <span className="flex flex-col gap-0.5">
          <span className="font-ds-sans text-ds-body text-ds-ink-900">
            {label}
          </span>
          {description ? (
            <span className="font-ds-sans text-ds-small text-ds-ink-600">
              {description}
            </span>
          ) : null}
        </span>
      </label>
    );
  }
);
Checkbox.displayName = "Checkbox";
